import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLocalizations } from '@/hooks/useLocalizations';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { logger } from '@/utils/logger';

export function useOpportunityNotifications() {
  const { user } = useAuth();
  const { lCommon } = useLocalizations();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Store lCommon in ref to avoid effect re-runs when translation functions change
  const lCommonRef = useRef(lCommon);
  useEffect(() => { lCommonRef.current = lCommon; }, [lCommon]);

  // Store navigate in ref to avoid dependency issues - navigate identity can change
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Stable callback using refs - no dependencies needed
  const navigateToOpportunities = useCallback(() => {
    navigateRef.current('/account/opportunities?tab=inbox');
  }, []);
  
  // Use stable channel name based on user ID to prevent subscription recreation
  // Unique per mount: a fixed name makes the Supabase client throw ("cannot add
  // postgres_changes callbacks after subscribe()") when another window or a
  // remount attaches to the same already-subscribed channel, crashing the page.
  const channelName = useMemo(
    () => (user?.id ? `opportunity-notifications-${user.id}-${Math.random().toString(36).slice(2)}` : null),
    [user?.id],
  );

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    // Fetch initial unread count
    const fetchUnreadCount = async () => {
      const { count, error } = await supabase
        .from('opportunities')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .in('status', ['released_to_provider', 'verified']);

      if (!error && count !== null) {
        setUnreadCount(count);
      }
    };

    fetchUnreadCount();

    // Set up realtime subscription for new opportunities with stable channel name
    if (!channelName) return;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'opportunities',
          filter: `recipient_id=eq.${user.id}`
        },
        (payload) => {
          logger.debug('Opportunity update received:', payload);
          
          const newOpp = payload.new;
          const oldOpp = payload.old;

          // Check if status changed to released_to_provider (newly qualified)
          if (
            newOpp.status === 'released_to_provider' && 
            oldOpp.status !== 'released_to_provider'
          ) {
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification - use ref to avoid dependency issues
            toast.success(lCommonRef.current('toast.new_inquiry_received.success', { defaultValue: 'New inquiry received' }), {
              description: `${newOpp.location || lCommonRef.current('labels.client', { defaultValue: 'Client' })} - ${newOpp.service_type || newOpp.subject}`,
              icon: <Mail className="h-4 w-4" />,
              action: {
                label: lCommonRef.current('actions.view', { defaultValue: 'View' }),
                onClick: navigateToOpportunities
              },
              duration: 5000
            });
          }

          // Decrement count when opportunity is viewed
          if (
            (newOpp.status === 'viewed_preview' || newOpp.status === 'viewed_full') &&
            (oldOpp.status === 'released_to_provider' || oldOpp.status === 'verified')
          ) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'opportunities',
          filter: `recipient_id=eq.${user.id}`
        },
        (payload) => {
          logger.debug('New opportunity inserted:', payload);
          
          const newOpp = payload.new;
          
          // Only count if already qualified
          if (newOpp.status === 'released_to_provider') {
            setUnreadCount(prev => prev + 1);
            
            toast.success(lCommonRef.current('toast.new_inquiry_received.success', { defaultValue: 'New inquiry received' }), {
              description: `${newOpp.location || lCommonRef.current('labels.client', { defaultValue: 'Client' })} - ${newOpp.service_type || newOpp.subject}`,
              icon: <Mail className="h-4 w-4" />,
              action: {
                label: lCommonRef.current('actions.view', { defaultValue: 'View' }),
                onClick: navigateToOpportunities
              },
              duration: 5000
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, channelName, navigateToOpportunities]); // Only depend on user.id and stable channel name

  // Memoize return value to ensure stable reference
  return useMemo(() => ({ unreadCount }), [unreadCount]);
}
